/**
 * The column picker's model: topics, one row per measure, and a staged draft.
 *
 * Pure, so the dialog in `ColumnPicker.tsx` is only layout. Three ideas carry
 * it (mock: `docs/column-picker-mock.html`, owner 2026-10-26):
 *
 * - **Topics are the server's categories.** `Price`, `Valuation`, `Momentum` —
 *   the same word the column's header band shows, so a column is found under
 *   the name it is later seen under. Nothing is invented here: a field with no
 *   category goes under *Other*, and the user's computed columns under *My
 *   columns*.
 * - **One row per base field.** The daily, weekly and monthly copies of a
 *   measure share a `baseField`; listing them as three rows in three places is
 *   most of why the old list was ~120 long. Here they are one row with a chip
 *   per timeframe.
 * - **Nothing is written until Apply.** The dialog edits an ordered list of
 *   shown fields; `applyDraft` turns it into the contract's column view once.
 */

import type { ScreenerColumn } from "@qufolio/model";

import type { ColumnSet } from "./column-sets.js";
import { SUGGESTED_BY_FIELD, SUGGESTED_COLUMNS } from "./suggested-columns.js";
import { LOGO_COLUMN, timeframeBadge, type UniverseColumn } from "./column-universe.js";

/** One line in the picker: a measure and its timeframe copies, daily first. */
export interface PickerRow {
  key: string;
  header: string;
  description: string;
  topic: string;
  variants: UniverseColumn[];
}

export const OTHER_TOPIC = "Other";
export const SUGGESTED_TOPIC = "Suggested";
export const MINE_TOPIC = "My columns";

/** Topics that lead, in reading order; any other category follows alphabetically. */
const TOPIC_ORDER = ["Price", "Volume", "Valuation", "Momentum", "Trend", "Volatility"];

function topicOf(column: UniverseColumn): string {
  if (column.authored) return MINE_TOPIC;
  const category = column.category?.trim();
  if (!category) return OTHER_TOPIC;
  return category.endsWith(" Indicators") ? category.slice(0, -" Indicators".length) : category;
}

/** Daily (no timeframe) first, then by unit size and count. */
const UNIT_RANK = { s: 0, mi: 1, h: 2, d: 3, w: 4, m: 5, q: 6, y: 7 } as const;
function timeframeRank(column: UniverseColumn): number {
  const tf = column.timeframe;
  if (!tf) return -1;
  return UNIT_RANK[tf.unit] * 1000 + tf.count;
}

/** The chip a variant wears: `D` for the screen's own timeframe, else `W`, `2W`, `M`. */
export function variantLabel(column: UniverseColumn): string {
  return column.timeframe ? timeframeBadge(column.timeframe) : "D";
}

/**
 * The universe plus the suggested computed columns this screen has not got yet
 * (`suggested-columns.ts`), as entries the picker can list and tick. Once one
 * is applied it is in the config, so the universe carries it and this adds
 * nothing — it then lists under *My columns* like any computed column.
 */
export function withSuggested(universe: readonly UniverseColumn[]): UniverseColumn[] {
  const have = new Set(universe.map((u) => u.field));
  const extra: UniverseColumn[] = SUGGESTED_COLUMNS.filter((s) => !have.has(s.column.field)).map(
    (s) => ({
      field: s.column.field,
      header: s.column.header ?? s.column.field,
      description: s.description,
      type: "number",
      baseField: s.column.field,
      category: SUGGESTED_TOPIC,
      origin: "compute",
      state: "pending",
      visible: false,
      order: Infinity,
    }),
  );
  return [...universe, ...extra];
}

/** The universe as picker rows, merged by base field, in universe order. */
export function pickerRows(universe: readonly UniverseColumn[]): PickerRow[] {
  const rows = new Map<string, PickerRow>();
  for (const column of universe) {
    // A computed column is its own measure even if its id looks like a base.
    const key = column.authored ? `mine:${column.field}` : column.baseField;
    let row = rows.get(key);
    if (!row) {
      row = { key, header: column.header, description: column.description, topic: topicOf(column), variants: [] };
      rows.set(key, row);
    }
    row.variants.push(column);
  }
  for (const row of rows.values()) {
    row.variants.sort((a, b) => timeframeRank(a) - timeframeRank(b));
    // Named after its first copy, which is the daily one whenever there is one.
    const first = row.variants[0]!;
    row.header = first.header;
    row.description = row.variants.find((v) => v.description)?.description ?? "";
  }
  return [...rows.values()];
}

/** Topic names with their rows, in display order. */
export function topicsOf(rows: readonly PickerRow[]): { topic: string; rows: PickerRow[] }[] {
  const byTopic = new Map<string, PickerRow[]>();
  for (const row of rows) {
    const list = byTopic.get(row.topic) ?? [];
    list.push(row);
    byTopic.set(row.topic, list);
  }
  const rank = (topic: string): [number, string] => {
    if (topic === MINE_TOPIC) return [3, topic];
    if (topic === OTHER_TOPIC) return [2, topic];
    const at = TOPIC_ORDER.indexOf(topic);
    return at >= 0 ? [0, String(at).padStart(3, "0")] : [1, topic];
  };
  return [...byTopic.entries()]
    .map(([topic, list]) => ({ topic, rows: list }))
    .sort((a, b) => {
      const [ra, ka] = rank(a.topic);
      const [rb, kb] = rank(b.topic);
      return ra - rb || ka.localeCompare(kb);
    });
}

/** Rows matching a free-text query on header, field or description. */
export function searchRows(rows: readonly PickerRow[], query: string): PickerRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter(
    (row) =>
      row.header.toLowerCase().includes(needle) ||
      row.description.toLowerCase().includes(needle) ||
      row.topic.toLowerCase().includes(needle) ||
      row.variants.some((v) => v.field.toLowerCase().includes(needle)),
  );
}

/** The fields on screen now, in grid order — the draft the dialog starts from. */
export function shownFields(universe: readonly UniverseColumn[]): string[] {
  return universe
    .filter((u) => u.visible)
    .sort((a, b) => a.order - b.order)
    .map((u) => u.field);
}

/** Toggle one field in the draft: on appends it, off removes it. */
export function toggleDraft(draft: readonly string[], field: string, on: boolean): string[] {
  const has = draft.includes(field);
  if (on && !has) return [...draft, field];
  if (!on && has) return draft.filter((f) => f !== field);
  return [...draft];
}

/** Move `from` to sit where `to` sits — the right pane's drag. */
export function moveInDraft(draft: readonly string[], from: string, to: string): string[] {
  if (from === to) return [...draft];
  const next = draft.filter((f) => f !== from);
  const at = next.indexOf(to);
  if (at < 0 || !draft.includes(from)) return [...draft];
  next.splice(draft.indexOf(from) < draft.indexOf(to) ? at + 1 : at, 0, from);
  return next;
}

/** Columns that stay when a preset replaces the rest: you still need to know whose row it is. */
const KEPT_ON_REPLACE = new Set([LOGO_COLUMN.field, "symbol"]);

/**
 * A preset applied to the draft.
 *
 * **Add** turns the set's columns on and leaves the rest. **Replace** shows the
 * set and the identity columns and nothing else — which is a draft, so Cancel
 * still undoes it, and a hidden column keeps everything set on it.
 *
 * A field this screen does not carry is skipped in silence, as `applyColumnSet`
 * does: a set is a suggestion, not a claim the columns exist.
 */
export function applyPreset(
  draft: readonly string[],
  universe: readonly UniverseColumn[],
  set: ColumnSet,
  mode: "add" | "replace",
): string[] {
  const present = new Set(universe.map((u) => u.field));
  const wanted = set.fields.filter((f) => present.has(f));
  const base = mode === "replace" ? draft.filter((f) => KEPT_ON_REPLACE.has(f)) : [...draft];
  for (const field of wanted) if (!base.includes(field)) base.push(field);
  return base;
}

/** How many of a set's fields this screen carries. */
export function presetSize(universe: readonly UniverseColumn[], set: ColumnSet): number {
  const present = new Set(universe.map((u) => u.field));
  return set.fields.filter((f) => present.has(f)).length;
}

/**
 * The draft as the contract's column view.
 *
 * Shown fields first, in the draft's order, each keeping whatever was set on it;
 * then every other configured column, hidden, in its old order — a hidden
 * column keeps its header, width and expression, as the grid's own hide does.
 * A field never configured and not shown is simply not listed: with a view,
 * unlisted means hidden.
 */
export function applyDraft(
  columns: readonly ScreenerColumn[],
  draft: readonly string[],
): ScreenerColumn[] {
  const configured = new Map(columns.map((c) => [c.field, c]));
  const shown = new Set(draft);
  const out: ScreenerColumn[] = draft.map((field) => {
    const existing = configured.get(field);
    if (existing) {
      const { hide: _hide, ...rest } = existing;
      return rest;
    }
    if (field === LOGO_COLUMN.field) return { ...LOGO_COLUMN };
    // A suggested column is a definition, not a server field: ticking it adds
    // the whole computed column, or the grid would get a field nothing serves.
    const suggested = SUGGESTED_BY_FIELD.get(field);
    return suggested ? { ...suggested.column } : { field };
  });
  for (const column of columns) {
    if (shown.has(column.field)) continue;
    out.push({ ...column, hide: true });
  }
  return out;
}
